import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiPost } from '../services/api';
import { getErrorMessage } from '../utils/getErrorMessage';

interface KioskCodeResponse {
  code: string;
  expiresAt: string;
  expiresInMinutes: number;
}

export function GenerateKioskCode() {
  const { user } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateCode = async () => {
    setLoading(true);
    setError('');
    setCopied(false);

    try {
      const response = await apiPost<KioskCodeResponse>('/kiosk/generate-code', {
        staffId: user?.staffID
      });

      if (response.success && response.data) {
        setCode(response.data.code);
        setExpiresAt(response.data.expiresAt);
      } else if (!response.success) {
        throw new Error(response.error?.message || 'Failed to generate code');
      } else {
        throw new Error('Failed to generate code');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to generate code'));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (code) {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const getTimeRemaining = () => {
    if (!expiresAt) {return null;}
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    if (diff <= 0) {return 'Expired';}
    const minutes = Math.floor(diff / 60000);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
  };

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Kiosk Intake</h3>
      <p style={{ color: '#666', marginBottom: '1rem' }}>
        Generate a code for clients to fill out their intake form on a tablet or kiosk.
      </p>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {!code ? (
        <button
          className="button-primary"
          onClick={generateCode}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate Kiosk Code'}
        </button>
      ) : (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
            padding: '1.5rem',
            background: '#f5f5f5',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              fontFamily: 'monospace',
              letterSpacing: '0.5rem',
              color: 'var(--color-primary-purple)'
            }}>
              {code}
            </div>
            <button
              onClick={copyCode}
              className="button-secondary"
              style={{ marginLeft: 'auto' }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {getTimeRemaining()}
          </p>

          <p style={{ marginBottom: '1rem' }}>
            <strong>Instructions:</strong>
          </p>
          <ol style={{ marginLeft: '1.5rem', marginBottom: '1rem', lineHeight: 1.8 }}>
            <li>Hand the tablet/device to the client</li>
            <li>Client goes to <a href="https://tacolcy.azurewebsites.net/kiosk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-purple)', textDecoration: 'underline' }}>https://tacolcy.azurewebsites.net/kiosk</a></li>
            <li>Client enters the 4-digit code: <strong>{code}</strong></li>
            <li>Client fills out the intake form</li>
          </ol>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              className="button-primary"
              onClick={generateCode}
              disabled={loading}
            >
              Generate New Code
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
